import { Store } from '@/store/pinia-class-component';
import { miniEntityStore } from '@/store/entities/types/miniEntityStore';
import { InstallationPoint } from '@/models/installationPoint/InstallationPoint';
import { getAllEntitiesByCriteria } from '@/store/entities/helper';
import { useRadiatorStore } from '@/store/entities/radiatorStore';
import { useDeviceStore } from '@/store/entities/deviceStore';
import { Tag } from '@/utils/canvas/drawingUtils';
import {
  AccessoryType,
  AdapterType,
  adapterTypeList,
  deviceTypeDetails,
  deviceTypes,
} from '@/enums/device';
import { DeviceClasses, DeviceType } from '@/models/devices/Device';
import { Adapter, BatteryPack } from '@/models/installationPoint/Accessory';
import { useEntitiesStore } from '@/store/entities/entitiesStore';

export function useInstallationPointStore() {
  return new InstallationPointStore();
}

@Store
export class InstallationPointStore extends miniEntityStore {
  private _installationPoints: Map<string, InstallationPoint>;
  recentAdapterList: Set<string>;

  private _byRoomGroupIdCache: Map<string, InstallationPoint[]> | null = null;

  constructor() {
    super();
    this._installationPoints = new Map();
    this.recentAdapterList = new Set();
  }

  private getByRoomGroupIdIndex(): Map<string, InstallationPoint[]> {
    if (!this._byRoomGroupIdCache) {
      const tempIndex = new Map<string, InstallationPoint[]>();
      this._installationPoints.forEach((ip) => {
        if (ip.billedInRoomGroupId) {
          const list = tempIndex.get(ip.billedInRoomGroupId) || [];
          list.push(ip);
          tempIndex.set(ip.billedInRoomGroupId, list);
        }
      });
      this._byRoomGroupIdCache = tempIndex;
    }
    return this._byRoomGroupIdCache;
  }

  private invalidateRoomGroupIdCache(): void {
    this._byRoomGroupIdCache = null;
  }

  get installationPoints(): Map<string, InstallationPoint> {
    return this._installationPoints;
  }

  get adaptersSortedByFrequency(): Map<string, number> {
    const adapterTypeMap: Map<string, number> = new Map();

    this._installationPoints.forEach((installationPoint) => {
      installationPoint.accessories
        .filter((adapter) => adapter.type === AccessoryType.SRT_ADAPTER)
        .forEach((adapter) => {
          const currentFrequency: number =
            adapterTypeMap.get(adapter.customizedNumber) || 0;
          adapterTypeMap.set(adapter.customizedNumber, currentFrequency + 1);
        });
    });

    return new Map([...adapterTypeMap.entries()].sort((a, b) => b[1] - a[1]));
  }

  get recommendedAdaptersPerSession() {
    let combinedList: Array<string> = [];
    const maxNumberOfRecommendedAdapters = 3;
    const numberOfFrequencyAdapters = 2;

    const adapterFrequencyIterator = this.adaptersSortedByFrequency.keys();
    for (let i = 0; i < numberOfFrequencyAdapters; i++) {
      combinedList.push(adapterFrequencyIterator.next().value);
    }
    let startingIndex = numberOfFrequencyAdapters;
    const recentAdapterArray = Array.from(this.recentAdapterList);

    recentAdapterArray.reverse().forEach((item) => {
      if (
        !combinedList.includes(item) &&
        startingIndex <
          maxNumberOfRecommendedAdapters + numberOfFrequencyAdapters
      ) {
        combinedList[startingIndex] = item;
        startingIndex++;
      }
    });
    combinedList = combinedList.filter((a) => a !== undefined);
    combinedList = combinedList.slice(0, maxNumberOfRecommendedAdapters);

    const recommendedAdaptersList: Array<AdapterType> = [];

    combinedList.forEach((item) => {
      const adapter = adapterTypeList.find(
        (adapter) => adapter.customizedNumber === item
      );
      if (adapter) {
        recommendedAdaptersList.push(adapter);
      }
    });

    return recommendedAdaptersList;
  }

  get uniqueSRTBatteryPackSerialNumbers() {
    const serialNumberMap = new Map<string, string>();
    this._installationPoints.forEach((installationPoint) => {
      installationPoint.accessories.forEach((accessory, index) => {
        if (accessory instanceof BatteryPack && accessory.serialNumber) {
          serialNumberMap.set(accessory.serialNumber, accessory.id);
        }
      });
    });
    return serialNumberMap;
  }

  isUniqueBatteryPack(batteryPack: BatteryPack): boolean {
    if (!batteryPack.serialNumber) return true;
    const existingBatteryPackUuid = this.uniqueSRTBatteryPackSerialNumbers.get(
      batteryPack.serialNumber
    );
    return (
      !existingBatteryPackUuid || existingBatteryPackUuid === batteryPack.id
    );
  }

  addToRecentAdapterList(adapter: AdapterType | Adapter) {
    const { customizedNumber } = adapter;

    if (this.recentAdapterList.has(customizedNumber)) {
      this.recentAdapterList.delete(customizedNumber);
    }
    this.recentAdapterList.add(customizedNumber);
  }

  getAllActiveDevices() {
    const activeDeviceIds = new Set<string>();
    this._installationPoints.forEach((installationPoint) => {
      if (installationPoint.activeDeviceId) {
        activeDeviceIds.add(installationPoint.activeDeviceId);
      }
    });
    const devices: DeviceClasses[] = [];
    useDeviceStore()
      .getDevices()
      .forEach((device) => {
        if (activeDeviceIds.has(device.id)) {
          devices.push(device);
        }
      });
    return devices;
  }

  getCentralWarmWaterHeatMeter() {
    const centralWarmWaterHeatMeters: DeviceClasses[] = [];
    getAllEntitiesByCriteria(
      this._installationPoints,
      (entity: InstallationPoint) => entity.isCentralAdvancedRegistrationMeter
    ).forEach((installationPoint) => {
      const device = useDeviceStore().devices.get(
        installationPoint.activeDeviceId
      );
      if (!device) {
        return;
      }
      centralWarmWaterHeatMeters.push(device);
    });
    return centralWarmWaterHeatMeters;
  }

  getInstallationPointById(id: string): InstallationPoint | undefined {
    return this._installationPoints.get(id);
  }

  getInstallationPointsByParentId(parentId: string): InstallationPoint[] {
    return getAllEntitiesByCriteria(
      this._installationPoints,
      (entity: InstallationPoint) => entity.getParentId() === parentId
    );
  }

  getInstallationPointsByRadiatorId(radiatorId: string): InstallationPoint[] {
    return getAllEntitiesByCriteria(
      this._installationPoints,
      (entity: InstallationPoint) => entity.radiatorId === radiatorId
    );
  }

  getInstallationPointsOnPlant(plantId: string): InstallationPoint[] {
    return getAllEntitiesByCriteria(
      this._installationPoints,
      (entity: InstallationPoint) => entity.plantId === plantId
    );
  }

  getAllInstallationPointsByArmId(armId: string): InstallationPoint[] {
    return getAllEntitiesByCriteria(
      this._installationPoints,
      (entity: InstallationPoint) => entity.advancedRegistrationById === armId
    );
  }

  getInstallationPointsByRoomGroupId(parentId: string): InstallationPoint[] {
    return this.getByRoomGroupIdIndex().get(parentId) || [];
  }

  getInstallationPointsByRoomId(roomId: string): InstallationPoint[] {
    const radiatorIds = useRadiatorStore()
      .getAllRadiatorsInRoom(roomId)
      .map((radiator) => radiator.id);
    const allParentIds = [...radiatorIds, roomId];
    return getAllEntitiesByCriteria(
      this._installationPoints,
      (entity: InstallationPoint) => allParentIds.includes(entity.getParentId())
    );
  }

  getNewInstallationPointOrdinal(roomId: string): number {
    const installationPoints = this.getInstallationPointsByRoomId(roomId);

    return installationPoints.length;
  }

  getFailedInstallationAttempt(roomId: string) {
    return this.getInstallationPointsByRoomId(roomId).filter(
      (ip) => !ip.activeDeviceId && ip.failedInstallationAttempt
    );
  }

  getFailedSrtInstallationAttempt(roomId: string) {
    return this.getInstallationPointsByRoomId(roomId).filter(
      (ip) =>
        !ip.activeDeviceId &&
        ip.failedInstallationAttempt &&
        ip.supportedDeviceType === deviceTypes.SmartRadiatorThermostat
    );
  }

  getUnmountedDevicesInRoom(roomId: string) {
    const installationPoints = this.getInstallationPointsByRoomId(roomId);
    return installationPoints.filter((ip) => {
      if (!ip.supportedDeviceType || ip.activeDeviceId) return false;
      return ip.isDeviceUnmounted();
    });
  }

  getUnmountedDevicesInRoomGroup(roomGroupId: string) {
    const installationPoints =
      this.getInstallationPointsByRoomGroupId(roomGroupId);
    return installationPoints.filter((ip) => {
      if (!ip.supportedDeviceType || ip.activeDeviceId) return false;
      return ip.isDeviceUnmounted();
    });
  }

  isInstallationPointDeletable(id: string) {
    const installationPoint = this._installationPoints.get(id);
    if (!installationPoint) {
      throw new Error('Installation point not found');
    }
    if (installationPoint.failedInstallationAttempt) {
      return true;
    }
    const devices = useDeviceStore().getDevicesByInstallationPointId(id);
    return devices.length <= 1;
  }

  addEntities(entities: InstallationPoint[]): void {
    this._installationPoints = new Map(
      entities.map(
        (entity) => [entity.id, entity] as [string, InstallationPoint]
      )
    );

    const tempIndex = new Map<string, InstallationPoint[]>();
    this._installationPoints.forEach((ip) => {
      const roomGroupId = ip.billedInRoomGroupId;
      if (roomGroupId) {
        const list = tempIndex.get(roomGroupId) || [];
        list.push(ip);
        tempIndex.set(roomGroupId, list);
      }
    });
    this._byRoomGroupIdCache = tempIndex;
  }

  addEntity(installationPoint: InstallationPoint): void {
    this._installationPoints.set(installationPoint.id, installationPoint);
    this.invalidateRoomGroupIdCache();
  }

  deleteEntity(entity: InstallationPoint): void {
    this._installationPoints.delete(entity.id);
    this.invalidateRoomGroupIdCache();
  }

  updateOrdinals(roomId: string): void {
    const installationPoints = this.getInstallationPointsByRoomId(roomId);
    installationPoints.sort((a, b) => a.ordinal - b.ordinal);
    installationPoints.forEach((installationPoint, index) => {
      installationPoint.ordinal = index;
    });
  }

  getTagsOfRoomgroup(roomgroupId: string): Record<string, Array<Tag>> {
    const installationPoints =
      this.getInstallationPointsByRoomGroupId(roomgroupId);

    if (installationPoints.length === 0) {
      return { ACTIVE: [], INACTIVE: [], UNMOUNTED: [] };
    }

    const counts = new Map<
      string,
      { active: number; inactive: number; unmounted: number }
    >();

    for (const ip of installationPoints) {
      const deviceType = ip.supportedDeviceType;
      if (!deviceType) continue;

      let entry = counts.get(deviceType);
      if (!entry) {
        entry = { active: 0, inactive: 0, unmounted: 0 };
        counts.set(deviceType, entry);
      }

      if (ip.activeDeviceId) {
        entry.active++;
      } else if (ip.failedInstallationAttempt) {
        entry.inactive++;
      } else if (ip.isDeviceUnmounted()) {
        entry.unmounted++;
      }
    }

    const tags: Record<string, Array<Tag>> = {
      ACTIVE: [],
      INACTIVE: [],
      UNMOUNTED: [],
    };

    counts.forEach((entry, deviceType) => {
      const deviceDetail = deviceTypeDetails[deviceType as DeviceType];
      const label = deviceDetail?.shortLabel;
      const color = deviceDetail?.color ?? '#000000';

      if (entry.active > 0) {
        tags.ACTIVE.push(new Tag(`${label}`, entry.active, color));
      }
      if (entry.inactive > 0) {
        tags.INACTIVE.push(new Tag(`${label} `, entry.inactive, color));
      }
      if (entry.unmounted > 0) {
        tags.UNMOUNTED.push(new Tag(`${label} `, entry.unmounted, color));
      }
    });

    return tags;
  }

  resetState(): void {
    this._installationPoints = new Map();
    this.invalidateRoomGroupIdCache();
  }
}
